import { useState, useEffect, useCallback } from "react";
import { useLocalization } from "@fluent/react";
import { useNotificationContext } from "cerulean";
import { DRAWER_TYPES } from "../../../RecipientDrawer";
import { getRecipientById } from "./BulkPaymentTable";

type DrawerForm = {
  setFieldValue: (field: string, value: unknown) => void;
};

const useRecipientDrawerState = (
  recipients: API.Recipient[],
  form: DrawerForm
) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();

  const [isRecipientDrawerOpen, setIsRecipientDrawerOpen] = useState(false);
  const [typeOfRecipientDrawer, setTypeOfRecipientDrawer] = useState<
    typeof DRAWER_TYPES.edit | typeof DRAWER_TYPES.add
  >(DRAWER_TYPES.add);
  const [selectedRecipientId, setSelectedRecipientId] = useState<
    API.RecipientId | undefined
  >(undefined);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | undefined>(
    undefined
  );
  const [customAlertCopy, setCustomAlertCopy] = useState<string | undefined>(
    undefined
  );

  // hack to prevent redirecting to homepage if user presses esc button
  // and also to close the recipient drawer when esc is pressed
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();
        setIsRecipientDrawerOpen(false);
      }
    };
    window.addEventListener("keydown", handleEsc, {
      capture: true,
      passive: false,
    });
    return () => {
      window.removeEventListener("keydown", handleEsc, { capture: true });
    };
  }, []);

  const handleCloseDrawer = useCallback(
    (recipientId?: API.RecipientId, rowIndex?: number) => {
      const recipient = recipientId
        ? getRecipientById(recipients, recipientId)
        : null;
      if (recipient && !recipient.ach_destination) {
        sendNotification({
          type: "negative",
          text: l10n.getString("add-ach-details-banner"),
        });
      } else if (typeof rowIndex !== "undefined") {
        form.setFieldValue(`payments.${rowIndex}.recipient`, recipient || "");
      }
      setCustomAlertCopy(undefined);
      setSelectedRowIndex(undefined);
      setSelectedRecipientId(undefined);
      setIsRecipientDrawerOpen(false);
    },
    [recipients, sendNotification, l10n, form]
  );

  const handleClickEditRecipient = useCallback(
    (recipientId: API.RecipientId, rowIndex?: number) => {
      // if user selects recipient with no ach details, we open the edit drawer
      // if user adds ach details, we have to attach the payment with the newly edited recipient
      // in order to attach it, we need to know which payment or which row to attach it to, therefore we pass in the row index
      if (typeof rowIndex !== "undefined") {
        setSelectedRowIndex(rowIndex);
      }
      setSelectedRecipientId(recipientId);
      setTypeOfRecipientDrawer(DRAWER_TYPES.edit);
      setIsRecipientDrawerOpen(true);
    },
    []
  );

  const handleClickAddRecipient = useCallback((rowIndex: number) => {
    setSelectedRowIndex(rowIndex);
    setTypeOfRecipientDrawer(DRAWER_TYPES.add);
    setIsRecipientDrawerOpen(true);
  }, []);

  return {
    isRecipientDrawerOpen,
    typeOfRecipientDrawer,
    selectedRecipientId,
    selectedRowIndex,
    customAlertCopy,
    setCustomAlertCopy,
    handleCloseDrawer,
    handleClickEditRecipient,
    handleClickAddRecipient,
  };
};

export default useRecipientDrawerState;
